import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config"; // Base API URL

const Challenges = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch reported posts on mount
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${config.BASE_URL}/reports`);
        if (res.data.success) {
          setPosts(res.data.data);
          setSelectedPost(res.data.data[0] || null);
        }
      } catch (error) {
        console.error("Fetch Posts Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Filtered posts based on search
  const filteredPosts = posts.filter(
    (post) =>
      post.postOwner?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.text?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Update selectedPost when search changes
  useEffect(() => {
    if (filteredPosts.length === 0) {
      setSelectedPost(null);
    } else if (!filteredPosts.includes(selectedPost)) {
      setSelectedPost(filteredPosts[0]);
    }
  }, [searchTerm, posts]);

  // Delete Post
  const handleDelete = async () => {
    if (!selectedPost) return;
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      setLoading(true);
      await axios.delete(`${config.BASE_URL}/posts/${selectedPost.id}`);

      setPosts((prevPosts) => {
        const updatedPosts = prevPosts.filter((p) => p.id !== selectedPost.id);

        const index = prevPosts.findIndex((p) => p.id === selectedPost.id);
        let newSelected = null;
        if (updatedPosts.length > 0) {
          newSelected = index === 0 ? updatedPosts[0] : updatedPosts[index - 1];
        }
        setSelectedPost(newSelected);

        return updatedPosts;
      });
    } catch (error) {
      console.error("Delete Post Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Loading Dialog */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <div className="w-6 h-6 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
            <span className="font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="mb-6 bg-white rounded-lg shadow p-4 flex justify-between items-center">
        <input
          type="text"
          placeholder="Search by text or user"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Side - Reported Posts Holder */}
        <div className="col-span-1 bg-white rounded-lg shadow p-4">
          <h2 className="font-semibold mb-3">Reported Posts</h2>
          <div className="flex flex-col gap-3">
            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => setSelectedPost(post)}
                  className={`cursor-pointer bg-white  rounded-lg shadow-lg p-3 flex flex-col gap-2 ${selectedPost?.id === post.id ? "border-2 border-blue-500 scale-105" : ""
                    }`}
                >
                  {/* User Info */}
                  <div className="flex items-center gap-2">
                    <img
                      src={
                        post.postOwner?.dp
                          ? `${config.BASE_URL}uploads/${post.postOwner.dp}`
                          : "https://via.placeholder.com/40"
                      }
                      alt="profile"
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <h2 className="text-sm font-semibold">{post.postOwner?.name}</h2>
                      <p className="text-xs text-blue-500">@{post.postOwner?.userName}</p>
                    </div>
                  </div>

                  {/* Post Text / Media Preview */}
                  {post.mediaType === "text" ? (
                    post.text && <p className="text-gray-700 text-xs line-clamp-3">{post.text}</p>
                  ) : (
                    <>
                      {post.text && <p className="text-gray-700 text-xs line-clamp-3">{post.text}</p>}
                      {post.image && (
                        <img
                          src={post.image ? `${config.BASE_URL}uploads/${post.image}` : undefined}
                          alt="post"
                          className="w-full h-40 object-cover rounded-lg"
                        />
                      )}
                    </>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No reported posts found.</p>
            )}
          </div>
        </div>

        {/* Right Side - Selected Post + Reporters */}
        <div className="col-span-2 flex flex-col gap-6">
          {/* Selected Post */}
          {selectedPost ? (
            <div className="bg-white rounded-lg shadow p-4 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <img
                  src={
                    selectedPost.postOwner?.dp
                      ? `${config.BASE_URL}uploads/${selectedPost.postOwner.dp}`
                      : "https://via.placeholder.com/40"
                  }
                  alt="profile"
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <h2 className="font-semibold">{selectedPost.postOwner?.name}</h2>
                  <p className="text-sm text-blue-500">@{selectedPost.postOwner?.userName}</p>
                </div>
              </div>

              {/* Conditional rendering based on mediaType */}
              {selectedPost.mediaType === "text" ? (
                selectedPost.text && <p className="text-gray-700 text-sm">{selectedPost.text}</p>
              ) : (
                <>
                  {selectedPost.text && <p className="text-gray-700 text-sm">{selectedPost.text}</p>}
                  {selectedPost.image && (
                    <img
                      src={selectedPost.image ? `${config.BASE_URL}uploads/${selectedPost.image}` : undefined}
                      alt="post"
                      className="w-full h-40 object-cover rounded-lg"
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-4 text-gray-500 text-center">
              No record found.
            </div>
          )}

          {/* Reported By */}
          {selectedPost && selectedPost.reportedBy && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Reported By</h3>
              <div className="flex flex-col gap-3">
                {selectedPost.reportedBy.map((user) => (
                  <div key={user.id} className="flex items-center gap-3">
                    <img
                      src={
                    user?.dp
                      ? `${config.BASE_URL}uploads/${user.dp}`
                      : "https://via.placeholder.com/40"
                  }
                      alt="reporter"
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <h4 className="font-medium">{user.name}</h4>
                      <p className="text-sm text-blue-500">@{user.userName}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delete Button */}
          {selectedPost && (
            <button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-semibold"
            >
              Delete Post
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Challenges;
